import React, { forwardRef, useImperativeHandle } from 'react';
import { flexRender } from '@tanstack/react-table';
import { StyledTableContainer, StyledTable, StyledTr, StyledTd, StyledInnerTd, StyledContainer } from './styles';
import TableHeader from './TableHeader';
import Pagination from './Pagination';
import { BaseTableProps, TanstackUncontrolledTableTableRef } from './types';
import { getCommonPinningStyles } from './utils/getCommonPinningStyles';
import { useClientControlledTableConfig } from './hooks/useClientControlledTableConfig';

/**
 * TanstackUncontrolledTable 컴포넌트는 정렬, 필터링, 고정 열 기능을 **내부 상태**로 관리하는 테이블을 렌더링합니다.
 * @template T - 테이블 데이터 타입
 * @param props - 컴포넌트 속성
 * @returns - 렌더링된 테이블 컴포넌트
 */
function TanstackUncontrolledTableInner<T>(
  props: BaseTableProps<T>,
  ref: React.ForwardedRef<TanstackUncontrolledTableTableRef>,
): JSX.Element {
  const {
    data,
    columns: originColumns,
    sortingState,
    filteringState,
    rowSelectionState,
    paginationState,
    columnPinning = [],
    rowCount,
  } = props;

  const { table } = useClientControlledTableConfig({
    data,
    columns: originColumns,
    sortingState,
    filteringState,
    rowSelectionState,
    paginationState,
    columnPinning,
    rowCount,
  });

  useImperativeHandle(ref, () => ({
    resetFilters: table.resetColumnFilters,
    resetSorting: table.resetSorting,
  }));

  return (
    <StyledContainer>
      <StyledTableContainer>
        <StyledTable width={table.getTotalSize()}>
          <TableHeader headerGroups={table.getHeaderGroups()} />
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <StyledTr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <StyledTd key={cell.id} {...getCommonPinningStyles(cell.column)}>
                    <StyledInnerTd>{flexRender(cell.column.columnDef.cell, cell.getContext())}</StyledInnerTd>
                  </StyledTd>
                ))}
              </StyledTr>
            ))}
          </tbody>
        </StyledTable>
      </StyledTableContainer>
      <Pagination
        hasPrev={table.getCanPreviousPage()}
        hasNext={table.getCanNextPage()}
        onPrev={(): void => table.previousPage()}
        onNext={(): void => table.nextPage()}
        onPageSelect={(e): void => table.setPageSize(Number(e.target.value))}
        onEnter={(e): void => {
          if (e.key === 'Enter') {
            const page = e.currentTarget.value ? Number(e.currentTarget.value) - 1 : 0;

            if (page + 1 > table.getPageCount()) {
              return;
            }

            table.setPageIndex(page);
          }
        }}
        pageSize={table.getState().pagination.pageSize}
        maxPageSize={table.getPageCount() ? table.getPageCount() : 1}
        pageIndex={table.getState().pagination.pageIndex}
      />
    </StyledContainer>
  );
}

const TanstackUncontrolledTable = forwardRef(TanstackUncontrolledTableInner) as unknown as <T>(
  props: BaseTableProps<T> & React.RefAttributes<TanstackUncontrolledTableTableRef>,
) => ReturnType<typeof TanstackUncontrolledTableInner>;

export default TanstackUncontrolledTable;
